import type { Address, AddressStored, CasAddressStored } from "@jti/fe-common";

import { CasBaseApi } from "../../CasBaseApi";
import { Endpoints } from "../../CasEndpoints";
import {
  convertCasAddressToStoredAddress,
  convertStoredAddressToCasAddress,
  prepareAddresses,
  prepareAndFindAddress,
} from "../../utils";
import type { CasAddressesApiContract } from "../CasAddressesApi.types";

/** @deprecated Legacy proxy implementation — superseded by EVO. To be deleted after production verification. */
export class CasAddressesApiLegacy extends CasBaseApi implements CasAddressesApiContract {
  readonly name = "CasAddressesApi";
  private Endpoints;
  constructor(
    baseUrl: string,
    protected tenantToken: string,
    protected ciamToken: string,
    protected userUuid: string
  ) {
    super(baseUrl, tenantToken, ciamToken, userUuid);
    this.Endpoints = Endpoints;
  }

  async getAddresses(): Promise<{ addresses: AddressStored[] }> {
    const { addresses } = await this.request<{
      addresses: CasAddressStored[];
    }>(this.replaceUuid(this.Endpoints.ProfilePath));

    return {
      addresses: prepareAddresses(addresses),
    };
  }

  async createAddress(address: Address): Promise<{
    addresses: AddressStored[];
    address: AddressStored;
  }> {
    const {
      customer: { addresses },
    } = await this.request<{
      customer: { addresses: CasAddressStored[] };
    }>(this.replaceUuid(this.Endpoints.StoreAddressesUrl), {
      method: "POST",
      body: JSON.stringify(convertStoredAddressToCasAddress(address)),
    });
    return {
      addresses: prepareAddresses(addresses),
      address: convertCasAddressToStoredAddress(addresses[addresses.length - 1]),
    };
  }

  async updateAddress(
    addressId: string,
    address: Address
  ): Promise<{
    addresses: AddressStored[];
    address?: AddressStored;
  }> {
    const url = this.formatEndpointUrl(this.Endpoints.UpdateAddressUrl, { ADDRESS_ID: addressId, UUID: this.userUuid });

    const {
      customer: { addresses },
    } = await this.request<{
      customer: { addresses: CasAddressStored[] };
    }>(url, {
      method: "PUT",
      body: JSON.stringify(convertStoredAddressToCasAddress(address)),
    });

    const { storedAddresses, updatedAddress } = prepareAndFindAddress(addresses, addressId);

    return {
      addresses: storedAddresses,
      address: updatedAddress,
    };
  }

  async setDefaultShippingAddress(addressId: string): Promise<{
    addresses: AddressStored[];
    address?: AddressStored;
  }> {
    const {
      customer: { addresses },
    } = await this.request<{
      customer: { addresses: CasAddressStored[] };
    }>(this.replaceAddressId(this.replaceUuid(this.Endpoints.UpdateDefaultShippingAddress), addressId), {
      method: "PUT",
      body: null,
    });

    const { storedAddresses, updatedAddress } = prepareAndFindAddress(addresses, addressId);

    return {
      addresses: storedAddresses,
      address: updatedAddress,
    };
  }

  async setDefaultBillingAddress(addressId: string): Promise<{
    addresses: AddressStored[];
    address?: AddressStored;
  }> {
    const {
      customer: { addresses },
    } = await this.request<{
      customer: { addresses: CasAddressStored[] };
    }>(this.replaceAddressId(this.replaceUuid(this.Endpoints.UpdateDefaultBillingAddress), addressId), {
      method: "PUT",
      body: null,
    });
    const { storedAddresses, updatedAddress } = prepareAndFindAddress(addresses, addressId);

    return {
      addresses: storedAddresses,
      address: updatedAddress,
    };
  }

  async deleteAddress(addressId: string): Promise<void> {
    await this.request<{
      customer: { addresses: CasAddressStored[] };
    }>(this.replaceAddressId(this.replaceUuid(this.Endpoints.UpdateAddressUrl), addressId), {
      method: "DELETE",
    });
  }
}
